<script lang='ts'>
export default {
  name: 'KkCheckboxSurveyAnswerDetail',
}
</script>

<script setup lang='ts'>
import type { Answer } from '../../../analysis/types/surveysDetail'
import { roundDecimal } from '../../../../modules/decimalCalc'
import { decimalPlace } from '../../../../modules/analysis'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Props {
  answers: Answer[]
  label: string
  compareLabel?: string
}

const props = defineProps<Props>()

const maxValue = computed<number>(() => {
  const values: number[] = []
  props.answers.forEach(answer => {
    values.push(answer.value)
    if (answer.compareValue) {
      values.push(answer.compareValue)
    }
  })
  return Math.max(...values)
})

const graphWidth = (value: number): string => {
  return `${ value / maxValue.value * 100 }%`
}
</script>

<template>
  <div class="answer">
    <ul class="answer__graphLabel">
      <li class="answer__graphLabel__item">
        {{ label }}
      </li>
      <li v-if="compareLabel" class="answer__graphLabel__item is-compare">
        {{ compareLabel }}
      </li>
    </ul>
    <ul v-if="answers.length" class="answer__items">
      <li v-for="answer in answers" :key="answer.id" class="answer__item">
        <p class="answer__item__label">{{ answer.label }}</p>
        <div class="answer__item__container">
          <div class="answer__item__graph">
            <p class="answer__item__graph__value">
              {{ roundDecimal(answer.value, decimalPlace).toLocaleString() }}{{ t('labels.countUnit') }}
            </p>
            <div class="answer__item__graph__bar" :style="{ width: graphWidth(answer.value) }" />
          </div>
          <div v-if="answer.compareValue" class="answer__item__graph">
            <p class="answer__item__graph__value">
              {{ roundDecimal(answer.compareValue, decimalPlace).toLocaleString() }}{{ t('labels.countUnit') }}
            </p>
            <div class="answer__item__graph__bar is-compare" :style="{ width: graphWidth(answer.compareValue) }" />
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.answer {
  &__graphLabel {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 16px;
    &__item {
      position: relative;
      margin: 0 16px;
      padding-left: 14px;
      font-size: 12px;
      line-height: 1.33;
      &::before {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #E9446A;
        content: '';
      }
      &.is-compare {
        &::before {
          opacity: .5;
        }
      }
    }
  }
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media screen and (max-width: 768px) {
      display: block;
    }
    &:not(:first-child) {
      padding-top: 16px;
      border-top: 1px solid #E4E7EB;
    }
    &:not(:last-child) {
      padding-bottom: 16px;
    }
    &__label {
      flex-basis: 26%;
      max-width: 258px;
      font-size: 14px;
      line-height: 1.71;
      @media screen and (max-width: 768px) {
        max-width: none;
      }
    }
    &__container {
      flex-basis: 74%;
      flex-grow: 1;
      @media screen and (max-width: 768px) {
        margin-top: 8px;
      }
    }
    &__graph {
      display: flex;
      align-items: center;
      &:not(:first-child) {
        margin-top: 8px;
      }
      &__value {
        flex-basis: 104px;
        min-width: 104px;
        padding-right: 8px;
        font-size: 12px;
        line-height: 1;
        text-align: right;
        white-space: nowrap;
      }
      &__bar {
        width: 0;
        height: 16px;
        background-color: #E9446A;
        transition: width .4s 1s;
        &.is-compare {
          opacity: .5;
        }
      }
    }
  }
}
</style>